/** @jsx jsx */

import type { HTMLAttributes } from 'react';
import { useMemo } from 'react';
import { useRef } from 'react';
import { forwardRef, useCallback, useState } from 'react';
import {
  addDays,
  formatISO,
  isAfter,
  isBefore,
  isFirstDayOfMonth,
  isLastDayOfMonth,
  isSameDay,
  isValid,
  subDays,
} from 'date-fns';
import type { CSSObject, WithDataAttributeProp } from '@balance-web/core';
import { getTestIdFromDataAttributes, jsx } from '@balance-web/core';
import { formatDateObj } from '@balance-web/date-input';
import { PopoverDialog, usePopover } from '@balance-web/popover';
import type { InputSizeType } from '@balance-web/text-input';
import { Adornment } from '@balance-web/text-input';
import { useTheme } from '@balance-web/theme';
import { Box } from '@balance-web/box';
import { Flex } from '@balance-web/flex';
import { Text } from '@balance-web/text';
import { CalendarIcon } from '@balance-web/icon';
import { useFieldContext } from '@balance-web/field';
import { makeIdFactory } from '@balance-web/utils';

import type { CalendarProps } from './Calendar';
import { Calendar } from './Calendar';
import { ClearButton, InputButton } from './styled';
import type { ISODate, ISODateRange } from './types';
import { useRangeSelection } from './useRangeSelection';
import type { RangeUpdate } from './useRangeSelection';
import { DATE_FORMATS, dateToString, parseDateWithFormats } from './utils';

const DateRangePickerIdFactory = makeIdFactory('date-range-picker', [
  'startInput',
  'endInput',
  'clearButton',
  'popover',
  'shortcuts',
  'calendar',
]);

type InternalDateState = {
  start?: Date;
  end?: Date;
  textStart: string;
  textEnd: string;
};

export type DateRangePickerProps = WithDataAttributeProp<{
  /** When true, the triggers will be disabled. */
  disabled?: boolean;
  /** Indicate that the value does not conform to the format expected by the application. */
  invalid?: boolean;
  /** Called when the value changes. */
  onChange: (value: ISODateRange | undefined) => void;
  /** Called when the value is cleared. */
  onClear?: () => void;
  /** The value of the calendar, displayed in the trigger. */
  value: ISODateRange | undefined;
  size?: InputSizeType;
  /** Additional props to pass to the input element. */
  id?: string;
  /** Class name added to the calendar popover dialog. */
  popoverClassName?: string;
}> &
  Pick<
    CalendarProps,
    'disabledDays' | 'fromMonth' | 'initialMonth' | 'modifiers' | 'toMonth'
  >;

export const DateRangePicker = ({
  disabled = false,
  invalid: invalidProp = false,
  value,
  // calendar props
  disabledDays,
  fromMonth,
  initialMonth,
  modifiers: consumerModifiers,
  toMonth,
  size = 'medium',
  data,
  onChange,
  onClear,
  popoverClassName,
  ...inputProps
}: DateRangePickerProps) => {
  // @ts-ignore Santise invalid dom attributes
  if ('blur' in inputProps && typeof inputProps.blur === 'function') {
    delete inputProps.blur;
  }

  const theme = useTheme();

  const fieldContext = useFieldContext();
  const fieldId = inputProps.id || fieldContext.id;
  const testIds = DateRangePickerIdFactory(getTestIdFromDataAttributes(data));

  const isInvalid = invalidProp || fieldContext.invalid;

  const startTextInputRef = useRef<HTMLInputElement>(null);
  const endTextInputRef = useRef<HTMLInputElement>(null);

  /**
   * We need dervied state to support temporary valid/invalid text entry
   * and date selection within calendar.
   *
   * Don't use effects to sync internal/external state. Use events.
   */
  const [internalDateState, _setInternalDateState] = useState<
    InternalDateState
  >({
    start: value?.start ? new Date(value?.start) : undefined,
    textStart: value?.start ? dateToString(value?.start) : '',
    end: value?.end ? new Date(value?.end) : undefined,
    textEnd: value?.end ? dateToString(value?.end) : '',
  });

  /**
   * Function that accepts partial internal state inputs and appends them to current internal state.
   * Don't use _setInternalDate directly, it will cause confusion.
   * */
  function setInternalDate(
    update:
      | ((
          prevInternalState: InternalDateState
        ) => {
          start?: Date | ISODate | null;
          end?: Date | ISODate | null;
          textStart?: string;
          textEnd?: string;
        })
      | {
          start?: Date | ISODate | null;
          end?: Date | ISODate | null;
          textStart?: string;
          textEnd?: string;
        }
      | null
  ) {
    _setInternalDateState((prevState) => {
      if (update === null) {
        return {
          start: undefined,
          end: undefined,
          textStart: '',
          textEnd: '',
        };
      }

      const stateUpdate =
        typeof update === 'function' ? update(prevState) : update;

      const newState = {
        ...prevState,
      };

      if (stateUpdate.start === null) {
        newState.start = undefined;
      } else if (stateUpdate?.start) {
        newState.start = new Date(stateUpdate.start);
      }

      if (stateUpdate.end === null) {
        newState.end = undefined;
      } else if (stateUpdate?.end) {
        newState.end = new Date(stateUpdate.end);
      }

      if (stateUpdate.textStart !== undefined) {
        newState.textStart = stateUpdate.textStart;
      }

      if (stateUpdate.textEnd !== undefined) {
        newState.textEnd = stateUpdate.textEnd;
      }

      return newState;
    });
  }

  // sync state with the popover dialog and the "focused input"
  const { isOpen, setOpen, dialog, trigger } = usePopover({
    placement: 'bottom-start',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 8],
        },
      },
    ],
  });

  const textInputStyles = useMemo(() => {
    const styles: CSSObject = {
      border: 'none',
      outline: 'none',
      fontSize:
        size === 'small'
          ? theme.typography.fontSize.small
          : theme.typography.fontSize.medium,
      background: 'none',
      width: 85,
    };

    return styles;
  }, [size, theme.typography.fontSize.medium, theme.typography.fontSize.small]);

  const onDateRangeChange = useCallback(
    (args: { startDate?: Date; endDate?: Date }) => {
      onChange({
        start: args.startDate
          ? (formatISO(args.startDate, { representation: 'date' }) as ISODate)
          : internalDateState.start
          ? (formatISO(internalDateState.start, {
              representation: 'date',
            }) as ISODate)
          : undefined,
        end: args.endDate
          ? (formatISO(args.endDate, { representation: 'date' }) as ISODate)
          : internalDateState.end
          ? (formatISO(internalDateState.end, {
              representation: 'date',
            }) as ISODate)
          : undefined,
      });
    },
    [internalDateState.end, internalDateState.start, onChange]
  );

  const setRange = (update: RangeUpdate | null) => {
    setInternalDate(
      update && {
        ...update,
        textStart: update.start ? dateToString(update.start) : '',
        textEnd: update.end ? dateToString(update.end) : '',
      }
    );
  };

  const { setSelectedInput, handleOpen, handleDayClick } = useRangeSelection({
    value,
    range: internalDateState,
    isOpen,
    setOpen,
    setRange,
    startInputRef: startTextInputRef,
    endInputRef: endTextInputRef,
    onRangeChange: onDateRangeChange,
  });

  /** ============= Text input things start */

  // Don't update state inside this function. It's supposed to be a state processor.
  const calculateNextState = (args: {
    textValue: string;
    calendarDate?: Date;
  }) => {
    const stateUpdate: {
      date?: Date;
      text: string;
    } = {
      text: args.textValue,
    };

    const date = parseDateWithFormats(args.textValue, DATE_FORMATS);

    if (date) {
      stateUpdate.date = date;
    }

    return stateUpdate;
  };

  const isStartDateValid = (startDate: Date, endDate?: Date) => {
    if (!isValid(startDate) || startDate.getFullYear() < 1000) {
      return false;
    }

    if (!endDate) {
      return true;
    }

    return (
      new Date(startDate).setHours(0, 0, 0, 0) <=
      new Date(endDate).setHours(0, 0, 0, 0)
    );
  };

  const isEndDateValid = (endDate: Date, startDate?: Date) => {
    if (!isValid(endDate) || endDate.getFullYear() < 1000) {
      return false;
    }

    if (!startDate) {
      return true;
    }

    return (
      new Date(endDate).setHours(0, 0, 0, 0) >=
      new Date(startDate).setHours(0, 0, 0, 0)
    );
  };

  /**
   * Emit change event if start date is valid.
   * If it's invalid, fall back to last valid end date or no date if no last valid end date found.
   */
  const commitValidStartTextValue = () => {
    const textValueAsDate = parseDateWithFormats(
      internalDateState.textStart,
      DATE_FORMATS
    );
    if (
      textValueAsDate &&
      isStartDateValid(textValueAsDate, internalDateState.end)
    ) {
      // Set text value again as formatted value
      setInternalDate({
        textStart: dateToString(textValueAsDate),
        start: textValueAsDate,
      });
      onDateRangeChange({
        startDate: textValueAsDate,
      });
      return true;
    } else {
      setInternalDate((prevDate) => {
        onDateRangeChange({
          startDate: prevDate.start,
        });

        return {
          start: prevDate.start,
          textStart: prevDate.start ? dateToString(prevDate.start) : '',
        };
      });
      return false;
    }
  };

  /**
   * Emit change event if end date is valid.
   * If it's invalid, fall back to last valid end date or no date if no last valid end date found.
   */
  const commitValidEndTextValue = () => {
    const textValueAsDate = parseDateWithFormats(
      internalDateState.textEnd,
      DATE_FORMATS
    );
    if (
      textValueAsDate &&
      isEndDateValid(textValueAsDate, internalDateState.start)
    ) {
      // Set text value again as formatted value
      setInternalDate({
        textEnd: dateToString(textValueAsDate),
        end: textValueAsDate,
      });
      onDateRangeChange({
        endDate: textValueAsDate,
      });
      return true;
    } else {
      setInternalDate((prevDate) => {
        onDateRangeChange({
          endDate: prevDate.end,
        });

        return {
          end: prevDate.end,
          textEnd: prevDate.end ? dateToString(prevDate.end) : '',
        };
      });
      return false;
    }
  };

  /** Text input things end =============  */

  // without an end date we only want the calendar to know about the start date
  // so the appropriate modifiers are applied
  const selectedDays = !internalDateState.end
    ? internalDateState.start
    : {
        from: internalDateState.start as Date,
        to: internalDateState.end as Date,
      };

  // stringify the internal date object for button labels
  const formattedStart = internalDateState.start
    ? formatDateObj(internalDateState.start)
    : undefined;

  // modifiers allow us to style the calendar for range input
  const modifiers = {
    ...consumerModifiers,
    lastOfMonth: (day: Date) => {
      return isLastDayOfMonth(day);
    },
    firstOfMonth: (day: Date) => {
      return isFirstDayOfMonth(day);
    },
    rangeStart: (day: Date) => {
      return Boolean(
        internalDateState.start && isSameDay(day, internalDateState.start)
      );
    },
    rangeEnd: (day: Date) => {
      return Boolean(
        internalDateState.end && isSameDay(day, internalDateState.end)
      );
    },
    rangeBetween: (day: Date) => {
      if (!internalDateState.start || !internalDateState.end) {
        return false;
      }
      if (
        isSameDay(day, internalDateState.start) ||
        isSameDay(day, internalDateState.end)
      ) {
        return false;
      }

      return (
        isAfter(day, internalDateState.start) &&
        isBefore(day, internalDateState.end)
      );
    },
  };
  const calendarProps = {
    disabledDays,
    fromMonth,
    initialMonth,
    modifiers,
    toMonth,
  };

  return (
    <Box
      data={{ testid: testIds.base(fieldId) }}
      data-balance-component="date-range-picker"
      position="relative"
    >
      <ButtonWrapper ref={trigger.ref}>
        <InputButton
          aria-label={ariaLabel('Choose start date', formattedStart)}
          disabled={disabled}
          invalid={isInvalid}
          isSelected={isOpen}
          size={size}
          onClick={handleOpen()}
          css={{
            paddingRight: `calc(${theme.spacing.small} * 9)`, // space for the clear button and calendar icon
          }}
          {...inputProps}
          {...trigger.props}
        >
          <Flex alignItems="center" flex="1">
            <input
              ref={startTextInputRef}
              css={textInputStyles}
              data-testid={testIds.startInput(fieldId)}
              placeholder="dd/mm/yyyy"
              type="text"
              value={internalDateState.textStart}
              onBlur={(e) => {
                if (
                  !isBlurToPickerControl(
                    e.relatedTarget,
                    endTextInputRef.current
                  )
                ) {
                  commitValidStartTextValue();
                }
              }}
              onChange={(e) => {
                const update = calculateNextState({
                  textValue: e.target.value,
                  calendarDate: internalDateState.start,
                });

                // Bail out of updating the start calendar date if start date text is after end date
                if (
                  update.date &&
                  internalDateState.end &&
                  update.date > internalDateState.end
                ) {
                  update.date = undefined;
                }
                setInternalDate({
                  start: update.date,
                  textStart: update.text,
                });
              }}
              onFocus={() => {
                setOpen(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setOpen(false);
                  return;
                }

                if (e.key === 'Enter' || e.key === 'Tab') {
                  e.preventDefault();
                  if (commitValidStartTextValue()) {
                    endTextInputRef.current?.select();
                    setSelectedInput('end');
                  }
                  return;
                }

                // Shortcut to select todays date
                if (e.key === 't') {
                  e.preventDefault();
                  const now = new Date();

                  if (!isStartDateValid(now, internalDateState.end)) {
                    return;
                  }

                  setInternalDate({
                    start: now,
                    textStart: dateToString(now),
                  });
                  onDateRangeChange({
                    startDate: new Date(),
                  });
                }

                if (e.key === 'ArrowUp') {
                  e.preventDefault();

                  const date = parseDateWithFormats(
                    internalDateState.textStart,
                    DATE_FORMATS
                  );

                  if (!date || !isStartDateValid(date, internalDateState.end)) {
                    return;
                  }

                  const datePlus1Day = addDays(date, 1);

                  setInternalDate({
                    start: datePlus1Day,
                    textStart: dateToString(datePlus1Day),
                  });
                  onDateRangeChange({
                    startDate: datePlus1Day,
                  });
                }

                if (e.key === 'ArrowDown') {
                  e.preventDefault();

                  const date = parseDateWithFormats(
                    internalDateState.textStart,
                    DATE_FORMATS
                  );

                  if (!date) {
                    return;
                  }

                  const dateMinus1Day = subDays(date, 1);

                  setInternalDate({
                    start: dateMinus1Day,
                    textStart: dateToString(dateMinus1Day),
                  });
                  onDateRangeChange({
                    startDate: dateMinus1Day,
                  });
                }
              }}
            />
            <span
              css={{
                paddingInlineStart: size === 'small' ? 0 : theme.spacing.small,
                paddingInlineEnd: theme.spacing.small,
              }}
            >
              <Text size="small">to</Text>
            </span>
            <input
              ref={endTextInputRef}
              css={textInputStyles}
              data-testid={testIds.endInput(fieldId)}
              placeholder="dd/mm/yyyy"
              type="text"
              value={internalDateState.textEnd}
              onBlur={(e) => {
                if (
                  !isBlurToPickerControl(
                    e.relatedTarget,
                    startTextInputRef.current
                  )
                ) {
                  commitValidEndTextValue();
                }
              }}
              onChange={(e) => {
                const update = calculateNextState({
                  textValue: e.target.value,
                  calendarDate: internalDateState.end,
                });

                // Bail out of updating the end calendar date if end date text is before end date
                if (
                  update.date &&
                  internalDateState.start &&
                  update.date < internalDateState.start
                ) {
                  update.date = undefined;
                }

                setInternalDate({ end: update.date, textEnd: update.text });
              }}
              onFocus={() => {
                setOpen(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setOpen(false);
                  return;
                }

                if (e.key === 'Enter' || e.key === 'Tab') {
                  setOpen(false);
                  if (commitValidEndTextValue()) {
                    endTextInputRef.current?.blur();
                  } else if (
                    e.key === 'Enter' &&
                    internalDateState.textEnd.trim() !== ''
                  ) {
                    e.preventDefault();
                  }
                  return;
                }

                // Shortcut to select todays date
                if (e.key === 't') {
                  e.preventDefault();

                  const now = new Date();

                  if (!isEndDateValid(now, internalDateState.start)) {
                    return;
                  }

                  setInternalDate({
                    end: now,
                    textEnd: dateToString(now),
                  });
                  onDateRangeChange({
                    endDate: new Date(),
                  });
                }

                if (e.key === 'ArrowUp') {
                  e.preventDefault();

                  const date = parseDateWithFormats(
                    internalDateState.textEnd,
                    DATE_FORMATS
                  );

                  if (!date) {
                    return;
                  }

                  const datePlus1Day = addDays(date, 1);

                  setInternalDate({
                    end: datePlus1Day,
                    textEnd: dateToString(datePlus1Day),
                  });
                  onDateRangeChange({
                    endDate: datePlus1Day,
                  });
                }

                if (e.key === 'ArrowDown') {
                  e.preventDefault();

                  const date = parseDateWithFormats(
                    internalDateState.textEnd,
                    DATE_FORMATS
                  );

                  if (!date || !isEndDateValid(date, internalDateState.start)) {
                    return;
                  }

                  const dateMinus1Day = subDays(date, 1);

                  setInternalDate({
                    end: dateMinus1Day,
                    textEnd: dateToString(dateMinus1Day),
                  });
                  onDateRangeChange({
                    endDate: dateMinus1Day,
                  });
                }
              }}
            />
          </Flex>
        </InputButton>
      </ButtonWrapper>

      {onClear &&
      !disabled &&
      (value?.start?.length ||
        value?.end?.length ||
        internalDateState.start ||
        internalDateState.textStart.length ||
        internalDateState.end ||
        internalDateState.textEnd.length) ? (
        <ClearButton
          data-testid={testIds.clearButton(fieldId)}
          size={size}
          onClick={() => {
            setInternalDate(null);
            onClear();
            handleOpen('start')();
          }}
        />
      ) : null}

      <Adornment
        align="right"
        css={{
          paddingRight:
            size === 'medium' ? theme.spacing.small : theme.spacing.xsmall,
          pointerEvents: 'none',
          position: 'absolute',
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 3,
        }}
      >
        <CalendarIcon
          color={
            disabled ? 'inputDisabled' : isInvalid ? 'critical' : 'textTertiary'
          }
          size={size}
        />
      </Adornment>

      <PopoverDialog
        key="range-dialog"
        ref={dialog.ref}
        className={popoverClassName}
        data-testid={testIds.popover(fieldId)}
        isVisible={isOpen}
        {...dialog.props}
      >
        <Calendar
          enableOutsideDaysClick
          data={{ testid: testIds.calendar(fieldId) }}
          month={internalDateState.start || internalDateState.end}
          numberOfMonths={2}
          selectedDays={selectedDays}
          onDayClick={handleDayClick}
          {...calendarProps}
        />
      </PopoverDialog>
    </Box>
  );
};

// Utils
// ------------------------------

function ariaLabel(prefix: string, day?: string) {
  return prefix + (day ? `, selected date is ${day}` : '');
}

function isBlurToPickerControl(
  relatedTarget: Element | null,
  otherInput: HTMLInputElement | null
) {
  return (
    Boolean(relatedTarget?.closest('[data-balance-component="calendar"]')) ||
    relatedTarget === otherInput
  );
}

// Styled Components
// ------------------------------

const ButtonWrapper = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>((props, ref) => {
  return (
    <div
      ref={ref}
      css={{
        display: 'flex',
      }}
      {...props}
    />
  );
});
